import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  Plus, Trash2, Edit, Users, DollarSign, CheckCircle, XCircle, 
  Clock, Copy, Gift, TrendingUp, Award, Search, RefreshCw
} from 'lucide-react';
import { getErrorMessage } from '../../utils/errorHandler';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDiscountManager() {
  const [discountCodes, setDiscountCodes] = useState([]);
  const [collaborators, setCollaborators] = useState([]);
  const [collaboratorStats, setCollaboratorStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('codes');
  
  // Create code dialog
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newCode, setNewCode] = useState({
    code: '',
    description: '',
    discount_type: 'percentage',
    discount_value: 10,
    max_uses: '',
    expires_at: ''
  });
  
  // Approve collaborator dialog
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [collaboratorToApprove, setCollaboratorToApprove] = useState(null);
  const [approveData, setApproveData] = useState({
    tier: 'starter',
    personal_code: '',
    discount_percentage: 10
  });

  const fetchDiscountCodes = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/discounts/admin/codes`);
      setDiscountCodes(response.data.codes || []);
    } catch (error) {
      console.error('Failed to fetch discount codes:', error);
    }
  }, []);

  const fetchCollaborators = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/discounts/admin/collaborators`);
      setCollaborators(response.data.collaborators || []);
    } catch (error) {
      console.error('Failed to fetch collaborators:', error);
    }
  }, []);

  const fetchCollaboratorStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/discounts/admin/collaborators/stats`);
      setCollaboratorStats(response.data);
    } catch (error) {
      console.error('Failed to fetch collaborator stats:', error);
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchDiscountCodes(),
        fetchCollaborators(),
        fetchCollaboratorStats()
      ]);
      setLoading(false);
    };
    loadData();
  }, [fetchDiscountCodes, fetchCollaborators, fetchCollaboratorStats]);

  const handleCreateCode = async () => {
    if (!newCode.code || !newCode.description) {
      toast.error('Please fill in code and description');
      return;
    }

    setCreating(true);
    try {
      await axios.post(`${API}/discounts/admin/codes`, {
        ...newCode,
        max_uses: newCode.max_uses ? parseInt(newCode.max_uses) : null,
        expires_at: newCode.expires_at || null
      });
      toast.success('Discount code created!');
      setShowCreateDialog(false);
      setNewCode({
        code: '',
        description: '',
        discount_type: 'percentage',
        discount_value: 10,
        max_uses: '',
        expires_at: ''
      });
      fetchDiscountCodes();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to create code'));
    } finally {
      setCreating(false);
    }
  };

  const handleToggleCode = async (code) => {
    try {
      await axios.patch(`${API}/discounts/admin/codes/${code.code_id}`, {
        is_active: !code.is_active
      });
      toast.success(`Code ${code.is_active ? 'deactivated' : 'activated'}`);
      fetchDiscountCodes();
    } catch (error) {
      toast.error('Failed to update code');
    }
  };

  const handleDeleteCode = async (code) => {
    if (!confirm(`Delete code ${code.code}?`)) return;
    
    try {
      await axios.delete(`${API}/discounts/admin/codes/${code.code_id}`);
      toast.success('Code deleted');
      fetchDiscountCodes();
    } catch (error) {
      toast.error('Failed to delete code');
    }
  };

  const handleApproveCollaborator = async () => {
    if (!approveData.personal_code) {
      toast.error('Please enter a personal code for the collaborator');
      return;
    }

    try {
      await axios.post(`${API}/discounts/admin/collaborators/approve`, {
        collaborator_id: collaboratorToApprove.collaborator_id,
        tier: approveData.tier,
        personal_code: approveData.personal_code,
        discount_percentage: approveData.discount_percentage
      });
      toast.success('Collaborator approved!');
      setShowApproveDialog(false);
      setCollaboratorToApprove(null);
      fetchCollaborators();
      fetchCollaboratorStats();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to approve'));
    }
  };

  const handleRejectCollaborator = async (collaborator) => {
    const reason = prompt('Enter rejection reason:');
    if (!reason) return;

    try {
      await axios.post(`${API}/discounts/admin/collaborators/reject`, {
        collaborator_id: collaborator.collaborator_id,
        reason
      });
      toast.success('Application rejected');
      fetchCollaborators();
    } catch (error) {
      toast.error('Failed to reject');
    }
  };

  const handleSuspendCollaborator = async (collaborator) => {
    if (!confirm(`Suspend ${collaborator.display_name}?`)) return;

    try {
      await axios.post(`${API}/discounts/admin/collaborators/suspend/${collaborator.collaborator_id}`);
      toast.success('Collaborator suspended');
      fetchCollaborators();
    } catch (error) {
      toast.error('Failed to suspend');
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Code copied!');
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return <Badge className="bg-green-500"><CheckCircle className="w-3 h-3 mr-1" /> Approved</Badge>;
      case 'pending':
        return <Badge className="bg-yellow-500"><Clock className="w-3 h-3 mr-1" /> Pending</Badge>;
      case 'rejected':
        return <Badge className="bg-red-500"><XCircle className="w-3 h-3 mr-1" /> Rejected</Badge>;
      case 'suspended':
        return <Badge className="bg-gray-500">Suspended</Badge>;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <RefreshCw className="w-6 h-6 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Overview */}
      {collaboratorStats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <Gift className="w-8 h-8 text-pink-500" />
                <div>
                  <p className="text-sm text-gray-400">Active Codes</p>
                  <p className="text-2xl font-bold text-white">
                    {discountCodes.filter(c => c.is_active).length}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <Users className="w-8 h-8 text-blue-500" />
                <div>
                  <p className="text-sm text-gray-400">Collaborators</p>
                  <p className="text-2xl font-bold text-white">
                    {collaboratorStats.total_collaborators}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <TrendingUp className="w-8 h-8 text-green-500" />
                <div>
                  <p className="text-sm text-gray-400">Total Referrals</p>
                  <p className="text-2xl font-bold text-white">
                    {collaboratorStats.total_referrals}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <DollarSign className="w-8 h-8 text-amber-500" />
                <div>
                  <p className="text-sm text-gray-400">Pending Payouts</p>
                  <p className="text-2xl font-bold text-white">
                    ${collaboratorStats.pending_payouts?.toFixed(2) || '0.00'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-800">
          <TabsTrigger value="codes" className="data-[state=active]:bg-pink-500 data-[state=active]:text-black">
            <Gift className="w-4 h-4 mr-2" /> Discount Codes
          </TabsTrigger>
          <TabsTrigger value="collaborators" className="data-[state=active]:bg-purple-500 data-[state=active]:text-black">
            <Users className="w-4 h-4 mr-2" /> Collaborators
          </TabsTrigger>
          <TabsTrigger value="pending" className="data-[state=active]:bg-yellow-500 data-[state=active]:text-black">
            <Clock className="w-4 h-4 mr-2" /> Pending ({collaborators.filter(c => c.status === 'pending').length})
          </TabsTrigger>
        </TabsList>

        {/* Discount Codes Tab */}
        <TabsContent value="codes">
          <Card className="bg-slate-900 border-slate-800">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-white">Discount Codes</CardTitle>
                <Button onClick={() => setShowCreateDialog(true)} className="bg-pink-500 hover:bg-pink-600">
                  <Plus className="w-4 h-4 mr-2" /> Create Code
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[400px]">
                <div className="space-y-3">
                  {discountCodes.length === 0 ? (
                    <p className="text-gray-500 text-center py-8">No discount codes yet</p>
                  ) : (
                    discountCodes.map((code) => (
                      <div 
                        key={code.code_id} 
                        className={`flex items-center justify-between p-4 rounded-lg ${
                          code.is_active ? 'bg-slate-800' : 'bg-slate-800/50 opacity-60'
                        }`}
                      >
                        <div className="flex items-center gap-4">
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-mono text-lg font-bold text-white">{code.code}</span>
                              <Button size="sm" variant="ghost" onClick={() => copyCode(code.code)}>
                                <Copy className="w-3 h-3" />
                              </Button>
                              {code.collaborator_id && (
                                <Badge variant="outline" className="text-purple-400 border-purple-400">
                                  Collaborator
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm text-gray-400">{code.description}</p>
                            <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                              <span>
                                {code.discount_type === 'percentage' 
                                  ? `${code.discount_value}% off` 
                                  : `$${code.discount_value} off`}
                              </span>
                              <span>Used: {code.current_uses}{code.max_uses ? `/${code.max_uses}` : ''}</span>
                              {code.expires_at && (
                                <span>Expires: {new Date(code.expires_at).toLocaleDateString()}</span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Switch 
                            checked={code.is_active} 
                            onCheckedChange={() => handleToggleCode(code)}
                          />
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            className="text-red-400 hover:text-red-300"
                            onClick={() => handleDeleteCode(code)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Collaborators Tab */}
        <TabsContent value="collaborators">
          <Card className="bg-slate-900 border-slate-800">
            <CardHeader>
              <CardTitle className="text-white">Active Collaborators</CardTitle>
              <CardDescription>Approved affiliate partners earning commission</CardDescription>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[400px]">
                <div className="space-y-3">
                  {collaborators.filter(c => c.status === 'approved').length === 0 ? (
                    <p className="text-gray-500 text-center py-8">No approved collaborators yet</p>
                  ) : (
                    collaborators.filter(c => c.status === 'approved').map((collab) => (
                      <div 
                        key={collab.collaborator_id} 
                        className="flex items-center justify-between p-4 bg-slate-800 rounded-lg"
                      >
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-white">{collab.display_name}</span>
                            <Badge className={collab.tier === 'pro' ? 'bg-amber-500' : 'bg-blue-500'}>
                              {collab.tier === 'pro' ? 'Pro 25%' : 'Starter 15%'}
                            </Badge>
                          </div>
                          {collab.user && (
                            <p className="text-sm text-gray-400">{collab.user.email}</p>
                          )}
                          <div className="flex items-center gap-4 mt-2 text-sm">
                            <span className="text-gray-400">
                              Code: <span className="font-mono text-pink-400">{collab.personal_code}</span>
                            </span>
                            <span className="text-gray-400">
                              Referrals: <span className="text-white">{collab.total_referrals}</span>
                            </span>
                            <span className="text-gray-400">
                              Earnings: <span className="text-green-400">${collab.total_earnings?.toFixed(2) || '0.00'}</span>
                            </span>
                            <span className="text-gray-400">
                              Pending: <span className="text-amber-400">${collab.pending_earnings?.toFixed(2) || '0.00'}</span>
                            </span>
                          </div>
                        </div>
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="text-red-400 border-red-400 hover:bg-red-400/10"
                          onClick={() => handleSuspendCollaborator(collab)}
                        >
                          Suspend
                        </Button>
                      </div>
                    ))
                  )}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Pending Applications Tab */}
        <TabsContent value="pending">
          <Card className="bg-slate-900 border-slate-800">
            <CardHeader>
              <CardTitle className="text-white">Pending Applications</CardTitle>
              <CardDescription>Review and approve collaborator applications</CardDescription>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[400px]">
                <div className="space-y-3">
                  {collaborators.filter(c => c.status === 'pending').length === 0 ? (
                    <p className="text-gray-500 text-center py-8">No pending applications</p>
                  ) : (
                    collaborators.filter(c => c.status === 'pending').map((collab) => (
                      <div 
                        key={collab.collaborator_id} 
                        className="p-4 bg-slate-800 rounded-lg border-l-4 border-yellow-500"
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2 mb-2">
                              <span className="font-semibold text-white">{collab.display_name}</span>
                              {getStatusBadge(collab.status)}
                            </div>
                            {collab.user && (
                              <p className="text-sm text-gray-400">{collab.user.email}</p>
                            )}
                            {collab.bio && (
                              <p className="text-sm text-gray-300 mt-2">{collab.bio}</p>
                            )}
                            <div className="flex items-center gap-4 mt-2 text-sm text-gray-400">
                              <span>Followers: {collab.follower_count?.toLocaleString()}</span>
                              <span>Payment: {collab.payment_email}</span>
                            </div>
                            {collab.social_links && Object.keys(collab.social_links).length > 0 && (
                              <div className="flex items-center gap-2 mt-2">
                                {collab.social_links.instagram && (
                                  <Badge variant="outline">IG: {collab.social_links.instagram}</Badge>
                                )}
                                {collab.social_links.youtube && (
                                  <Badge variant="outline">YT</Badge>
                                )}
                                {collab.social_links.twitter && (
                                  <Badge variant="outline">X: {collab.social_links.twitter}</Badge>
                                )}
                              </div>
                            )}
                          </div>
                          <div className="flex gap-2">
                            <Button 
                              size="sm" 
                              className="bg-green-500 hover:bg-green-600"
                              onClick={() => {
                                setCollaboratorToApprove(collab);
                                setApproveData({
                                  tier: collab.follower_count >= 50000 ? 'pro' : 'starter',
                                  personal_code: collab.display_name.toUpperCase().replace(/\s+/g, '').slice(0, 10),
                                  discount_percentage: 10
                                });
                                setShowApproveDialog(true);
                              }}
                            >
                              <CheckCircle className="w-4 h-4 mr-1" /> Approve
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="text-red-400 border-red-400"
                              onClick={() => handleRejectCollaborator(collab)}
                            >
                              <XCircle className="w-4 h-4 mr-1" /> Reject
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Code Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="bg-slate-900 border-slate-800">
          <DialogHeader>
            <DialogTitle className="text-white">Create Discount Code</DialogTitle>
            <DialogDescription>Create a new promotional discount code</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-white">Code *</Label>
              <Input
                placeholder="e.g., SAVE20"
                value={newCode.code}
                onChange={(e) => setNewCode({ ...newCode, code: e.target.value.toUpperCase() })}
                className="bg-slate-800 border-slate-700 text-white"
              />
            </div>
            <div>
              <Label className="text-white">Description *</Label>
              <Input
                placeholder="e.g., Holiday sale 20% off"
                value={newCode.description}
                onChange={(e) => setNewCode({ ...newCode, description: e.target.value })}
                className="bg-slate-800 border-slate-700 text-white"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-white">Type</Label>
                <Select 
                  value={newCode.discount_type} 
                  onValueChange={(value) => setNewCode({ ...newCode, discount_type: value })}
                >
                  <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">Percentage</SelectItem>
                    <SelectItem value="fixed">Fixed Amount</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-white">Value</Label>
                <Input
                  type="number"
                  placeholder={newCode.discount_type === 'percentage' ? '10' : '5'}
                  value={newCode.discount_value}
                  onChange={(e) => setNewCode({ ...newCode, discount_value: parseFloat(e.target.value) })}
                  className="bg-slate-800 border-slate-700 text-white"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-white">Max Uses (optional)</Label>
                <Input
                  type="number"
                  placeholder="Unlimited"
                  value={newCode.max_uses}
                  onChange={(e) => setNewCode({ ...newCode, max_uses: e.target.value })}
                  className="bg-slate-800 border-slate-700 text-white"
                />
              </div>
              <div>
                <Label className="text-white">Expires (optional)</Label>
                <Input
                  type="date"
                  value={newCode.expires_at}
                  onChange={(e) => setNewCode({ ...newCode, expires_at: e.target.value })}
                  className="bg-slate-800 border-slate-700 text-white"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateCode} disabled={creating} className="bg-pink-500 hover:bg-pink-600">
              {creating ? 'Creating...' : 'Create Code'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Approve Collaborator Dialog */}
      <Dialog open={showApproveDialog} onOpenChange={setShowApproveDialog}>
        <DialogContent className="bg-slate-900 border-slate-800">
          <DialogHeader>
            <DialogTitle className="text-white">Approve Collaborator</DialogTitle>
            <DialogDescription>
              Configure commission tier and personal discount code for {collaboratorToApprove?.display_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-white">Commission Tier</Label>
              <Select 
                value={approveData.tier} 
                onValueChange={(value) => setApproveData({ ...approveData, tier: value })}
              >
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="starter">Starter (15% commission)</SelectItem>
                  <SelectItem value="pro">Pro (25% commission)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-white">Personal Code *</Label>
              <Input
                placeholder="e.g., JOHN10"
                value={approveData.personal_code}
                onChange={(e) => setApproveData({ ...approveData, personal_code: e.target.value.toUpperCase() })}
                className="bg-slate-800 border-slate-700 text-white"
              />
              <p className="text-xs text-gray-400 mt-1">This code will be assigned to the collaborator</p>
            </div>
            <div>
              <Label className="text-white">Discount for Followers (%)</Label>
              <Input
                type="number"
                value={approveData.discount_percentage}
                onChange={(e) => setApproveData({ ...approveData, discount_percentage: parseInt(e.target.value) })}
                className="bg-slate-800 border-slate-700 text-white"
              />
              <p className="text-xs text-gray-400 mt-1">How much discount followers get when using the code</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowApproveDialog(false)}>Cancel</Button>
            <Button onClick={handleApproveCollaborator} className="bg-green-500 hover:bg-green-600">
              Approve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
